import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import {
  DEFAULT_COUNTRY,
  getCountryConfig,
  isSupportedCountry,
  resolveCountry,
} from "./countryConfig";
import { isValidNumberForRegion } from "./phoneRegionValidation";

export const PHONE_EXACTLY_TEN_MSG = "Phone number must be exactly 10 digits.";

/** Strip non-digits and cap length (default 10). */
export function digitsOnlyPhone(value, maxLen = 10) {
  return String(value ?? "").replace(/\D/g, "").slice(0, maxLen);
}

/**
 * National storage — strips calling-code prefixes when country is known.
 * Legacy (no country): strips +1 / +91 and caps at 10 digits (unchanged US behavior).
 */
export function nationalPhoneForStorage(phone, country) {
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";

  if (country && isSupportedCountry(country)) {
    const config = getCountryConfig(country);
    const dialCode = config.phoneCountryCode.replace("+", "");
    let national = d;
    if (national.startsWith(dialCode) && national.length > dialCode.length) {
      national = national.slice(dialCode.length);
    }
    if (national.startsWith("0")) {
      national = national.replace(/^0+/, "");
    }
    return national.slice(0, config.nationalMaxLength);
  }

  // Legacy path — preserve existing US/India behavior
  if (d.length === 11 && d[0] === "1") return d.slice(1);
  if (d.length === 12 && d.startsWith("91")) return d.slice(2);
  if (d.length > 10) return d.slice(-10);
  return d;
}

/**
 * Build E.164 from stored fields. Twilio / Firebase Auth entry point.
 */
export function phoneToE164({ phoneCountryCode, phoneNumber, country } = {}) {
  const resolvedCountry = country && isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolvedCountry);
  const code = (phoneCountryCode || config.phoneCountryCode).replace(/\D/g, "");
  const national = nationalPhoneForStorage(phoneNumber, resolvedCountry);
  if (!national) return "";
  return `+${code}${national}`;
}

/**
 * Resolve phone fields from a record (handles legacy US-only docs).
 */
export function resolvePhoneFields(record = {}, company = {}) {
  const country = resolveCountry(record, company);
  const config = getCountryConfig(country);
  const phoneCountryCode = record.phoneCountryCode || config.phoneCountryCode;
  const phoneNumber = nationalPhoneForStorage(
    record.phoneNumber ?? record.mobileNumber ?? record.phone ?? "",
    country
  );
  return { country, phoneCountryCode, phoneNumber };
}

/**
 * Normalize phone fields before Firestore/API writes.
 */
export function normalizePhoneFields(fields = {}, companyCountry) {
  const country = resolveCountry(fields, { country: companyCountry });
  const config = getCountryConfig(country);
  return {
    country,
    phoneCountryCode: fields.phoneCountryCode || config.phoneCountryCode,
    phoneNumber: nationalPhoneForStorage(fields.phoneNumber ?? fields.mobileNumber, country),
  };
}

function parseForCountry(nationalNumber, country) {
  const config = getCountryConfig(country);
  const national = nationalPhoneForStorage(nationalNumber, country);
  if (!national) return null;
  return parsePhoneNumberFromString(national, config.libPhoneCountry);
}

export function isValidPhoneForCountry(phone, country = DEFAULT_COUNTRY) {
  const resolved = isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolved);
  const national = nationalPhoneForStorage(phone, resolved);
  if (!national) return false;

  const region = config.libPhoneCountry;
  const regionCheck = isValidNumberForRegion(national, region);
  if (!regionCheck.valid) return false;

  const e164 = `${config.phoneCountryCode}${national}`.replace(/\s/g, "");
  const e164Parsed = parsePhoneNumberFromString(e164);
  if (!e164Parsed?.isValid() || e164Parsed.country !== region) {
    return false;
  }

  return true;
}

/**
 * Normalize and validate phone fields before Firestore/API writes.
 * @returns {{ ok: boolean, error?: string, fields: ReturnType<typeof normalizePhoneFields> }}
 */
export function validateAndNormalizePhone(
  input = {},
  companyCountry,
  { allowEmpty = false, skipValidation = false } = {}
) {
  const fields = normalizePhoneFields(input, companyCountry);
  if (skipValidation) {
    return { ok: true, fields };
  }
  if (!fields.phoneNumber) {
    if (allowEmpty) return { ok: true, fields };
    return { ok: false, error: "Mobile is Required", fields };
  }
  if (!isValidPhoneForCountry(fields.phoneNumber, fields.country)) {
    return { ok: false, error: getInvalidPhoneMessage(fields.country), fields };
  }
  return { ok: true, fields };
}

/**
 * National 10-digit phone validation (legacy).
 * @param {string} number
 * @param {{ allowEmpty?: boolean }} opts
 */
export function validateTenDigitMobile(number, { allowEmpty = true } = {}) {
  const digits = digitsOnlyPhone(number, 10);
  if (!digits) {
    return allowEmpty ? "" : "Mobile is Required";
  }
  if (!/^\d{10}$/.test(digits)) {
    return PHONE_EXACTLY_TEN_MSG;
  }
  return "";
}

export const INVALID_NANP_MOBILE_MSG =
  "Enter a valid US, Canada, or other +1 area mobile number.";

export const INVALID_STORED_SMS_MOBILE_MSG =
  "Enter a valid US/Canada/+1 or India mobile number.";

export function getInvalidPhoneMessage(country = DEFAULT_COUNTRY) {
  const config = getCountryConfig(country);
  if (country === "US" || country === "CA") {
    return `Enter a valid ${config.name} mobile number. This number appears to belong to another country.`;
  }
  return `Enter a valid ${config.name} mobile number.`;
}

/**
 * Valid +1 NANP mobile (US, Canada, Puerto Rico, USVI, etc.).
 * Twilio SMS uses +1; do not force country=US or Canada numbers fail.
 */
export function isValidNanpSmsNationalMobile(phone) {
  const ten = nationalPhoneForStorage(phone);
  if (!/^\d{10}$/.test(ten)) return false;
  const parsed = parsePhoneNumberFromString(`+1${ten}`);
  return Boolean(parsed?.isValid() && parsed.countryCallingCode === "1");
}

/** India mobiles stored as national 10 digits (Firebase Auth may use +91). */
export function isValidIndiaSmsNationalMobile(phone) {
  const ten = nationalPhoneForStorage(phone);
  if (!/^\d{10}$/.test(ten)) return false;
  const parsed = parsePhoneNumberFromString(`+91${ten}`, "IN");
  return Boolean(parsed?.isValid());
}

/** Employee/user storage: country-aware or legacy NANP/India. */
export function isValidStoredSmsNationalMobile(phone, country) {
  if (country && isSupportedCountry(country)) {
    return isValidPhoneForCountry(phone, country);
  }
  return (
    isValidNanpSmsNationalMobile(phone) || isValidIndiaSmsNationalMobile(phone)
  );
}

/** Alias kept for SMS callers — means NANP (+1), not US-only. */
export function isValidUsSmsNationalMobile(phone) {
  return isValidNanpSmsNationalMobile(phone);
}

/** SMS-ready: valid for any supported country or legacy NANP/India. */
export function isValidSmsPhone(phone, country, phoneCountryCode) {
  if (country && isSupportedCountry(country)) {
    return isValidPhoneForCountry(phone, country);
  }
  if (phoneCountryCode) {
    const national = String(phone ?? "").replace(/\D/g, "");
    if (!national) return false;
    const e164 = `${phoneCountryCode}${national}`.replace(/\s/g, "");
    const parsed = parsePhoneNumberFromString(e164);
    return Boolean(parsed?.isValid());
  }
  return isValidStoredSmsNationalMobile(phone);
}

export function validatePhoneForCountry(
  number,
  country = DEFAULT_COUNTRY,
  { allowEmpty = true } = {}
) {
  const trimmed = String(number ?? "").trim();
  if (!trimmed) {
    return allowEmpty ? "" : "Mobile is Required";
  }
  return isValidPhoneForCountry(trimmed, country)
    ? ""
    : getInvalidPhoneMessage(country);
}

export function validateNanpSmsMobile(number, { allowEmpty = true } = {}) {
  const tenDigit = validateTenDigitMobile(number, { allowEmpty });
  if (tenDigit) return tenDigit;
  if (!String(number ?? "").trim()) return "";
  return isValidNanpSmsNationalMobile(number) ? "" : INVALID_NANP_MOBILE_MSG;
}

/** Country-aware validation; falls back to legacy when country omitted. */
export function validateStoredSmsMobile(
  number,
  { allowEmpty = true, country } = {}
) {
  if (country && isSupportedCountry(country)) {
    return validatePhoneForCountry(number, country, { allowEmpty });
  }
  const tenDigit = validateTenDigitMobile(number, { allowEmpty });
  if (tenDigit) return tenDigit;
  if (!String(number ?? "").trim()) return "";
  return isValidStoredSmsNationalMobile(number) ? "" : INVALID_STORED_SMS_MOBILE_MSG;
}

/** E.164 list for Twilio from employee/user records (legacy + new shape). */
export function phoneRecordsToE164List(records = []) {
  return [
    ...new Set(
      records
        .map((item) => {
          const { country, phoneCountryCode, phoneNumber } = resolvePhoneFields(item);
          if (!phoneNumber) return "";
          if (isValidSmsPhone(phoneNumber, country, phoneCountryCode)) {
            return phoneToE164({ country, phoneCountryCode, phoneNumber });
          }
          return "";
        })
        .filter(Boolean)
    ),
  ];
}

/** E.164 for Firebase Auth `createUser` (Admin SDK requirement). */
export function phoneForFirebaseAuth(phone, country, phoneCountryCode) {
  if (country && isSupportedCountry(country)) {
    const e164 = phoneToE164({
      country,
      phoneCountryCode,
      phoneNumber: phone,
    });
    if (e164) {
      const parsed = parsePhoneNumberFromString(e164);
      if (parsed?.isValid()) return e164;
    }
  }

  // Legacy path
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 11 && d[0] === "1") {
    const n = d.slice(1);
    if (n.length === 10) {
      const parsed = parsePhoneNumberFromString(`+1${n}`);
      if (parsed?.isValid()) return `+1${n}`;
    }
  }
  if (d.length === 10) {
    const us = `+1${d}`;
    const parsedUs = parsePhoneNumberFromString(us);
    if (parsedUs?.isValid()) return us;
    const inMobile = `+91${d}`;
    const parsedIn = parsePhoneNumberFromString(inMobile, "IN");
    if (parsedIn?.isValid()) return inMobile;
    return us;
  }
  const trimmed = String(phone).trim();
  if (trimmed.startsWith("+")) return trimmed.replace(/\s/g, "");
  return `+${d}`;
}
